import { useState, useEffect } from 'react';
import type { UserRole } from '@/shared/types';

interface UserIdentifiersProps {
  isAuthenticated: boolean;
  userSub: string | undefined;
  role: UserRole | null;
}

interface UserIdentifiers {
  rawUserId: string | null;
  rawProviderId: string | null;
  rawBusinessOwnerId: string | null;
  userId: string;
  providerId: string;
  businessOwnerId: string;
}

/**
 * Hook to manage user identifiers based on authentication state and role
 */
export const useUserIdentifiers = ({
  isAuthenticated,
  userSub,
  role,
}: UserIdentifiersProps): UserIdentifiers => {
  const [rawUserId, setUserId] = useState<string | null>(null);
  const [rawProviderId, setProviderId] = useState<string | null>(null);
  const [rawBusinessOwnerId, setBusinessOwnerId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!isAuthenticated || !userSub) {
      // Reset all IDs when not authenticated
      setUserId(null);
      setProviderId(null);
      setBusinessOwnerId(null);
      return;
    }

    // Set the user ID
    setUserId(userSub);

    // Set providerId if the user has a provider role (including pending providers)
    if (
      role === 'independent_provider' ||
      role === 'shop_owner' ||
      role === 'staff_provider' ||
      role === 'pending_provider' ||
      role === 'pending_staff'
    ) {
      setProviderId(userSub);
    } else {
      setProviderId(null);
    }

    // Set businessOwnerId if the user is a shop owner
    if (role === 'shop_owner') {
      setBusinessOwnerId(userSub);
    } else {
      setBusinessOwnerId(null);
    }
  }, [isAuthenticated, userSub, role]);

  // Non-nullable string IDs (empty string if null)
  const userId = rawUserId || '';
  const providerId = rawProviderId || '';
  const businessOwnerId = rawBusinessOwnerId || '';

  return {
    rawUserId,
    rawProviderId,
    rawBusinessOwnerId,
    userId,
    providerId,
    businessOwnerId,
  };
};
